import { computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useCardOptions } from "./useCardOptions";
import { useCardLocalOptions } from "./useCardLocalOptions";
import { useCardDownload } from "./useCardDownload";
import { useChartData } from "./useChartData";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { CHART_TYPES, NPS_BUCKETS, SINGLE_SERIES_CHART_TYPES } from "@/components/Survey/ResultsV2/constants";
import { TABLE_TILE, resolveChartType } from "@/components/Survey/ResultsV2/cardOptionRules";
import { useChartTile } from "./useChartTile";
import { isMultipleLikeCard } from "@utils/hiddenCardQuestionType";

export function useChartDisplay(card) {
  const store = useResultsV2Store();
  const { availableCards } = storeToRefs(store);
  const { mergedOptions } = useCardOptions(card);

  const chartData = useChartData(card);
  const {
    questionMeta,
    effectiveQuestionId,
    cardType,
    codeLabels,
    isMatrix,
    isMatrixMultiple,
    cardName,
    populationsBarData,
    matrixItemsBarData,
    stackedData,
    matrixChoiceCodes,
    matrixChoiceLabels,
    pieData,
    radarAxes,
    radarSeries,
    radarScale,
    matrixTableData,
    respondents,
    populationRespondents,
    displayedPopulations,
    singlePopulation,
    shouldIncludeWeights,
    title,
    activeIteration,
    loopIterationOptions,
    showSentiment,
    isNumeric,
    numericStats,
  } = chartData;

  const { downloadCardAsImage, copyCardToClipboard, copied } = useCardDownload();

  // Custom base label — custom_base stores a question id, not a card id
  const customBaseLabel = computed(() => {
    const baseId = card.value?.options?.custom_base;
    if (!baseId) return null;
    const base = availableCards.value.find((c) => c.questions?.some((q) => q.id === baseId));
    return base?.name || null;
  });

  // Chart type resolution
  const isMultipleLike = computed(() => isMultipleLikeCard(questionMeta.value));
  const { viewOptions, hasSubtotals, tile: chartTile, chartType } = useChartTile(card, questionMeta, displayedPopulations);

  const displayMode = computed(() => (chartTile.value === TABLE_TILE ? TABLE_TILE : "chart"));

  const storableChartType = computed(() =>
    resolveChartType(questionMeta.value, card.value?.options || {}, viewOptions.value, { hasSubtotals: hasSubtotals.value }),
  );
  const { updateLocalOption } = useCardLocalOptions(card);
  watch([storableChartType, isMultipleLike, hasSubtotals], () => {
    if (!isMultipleLike.value && !hasSubtotals.value) return;
    const selected = card.value?.options?.chart_type;
    if (cardType.value === "q_ranking" && selected?.endsWith("_stacked_100")) return;
    if (selected && selected !== storableChartType.value) updateLocalOption("chart_type", storableChartType.value);
  }, { immediate: true });

  const isNpsChartType = computed(() => chartType.value === CHART_TYPES.NPS);

  const hasMultiplePopulations = computed(() => populationsBarData.value.length > 1);

  const isPie = computed(() =>
    [CHART_TYPES.PIE, CHART_TYPES.PIE_EXPLODED, CHART_TYPES.DOUGHNUT].includes(chartType.value),
  );
  const isStacked = computed(() =>
    [CHART_TYPES.BAR_STACKED_100, CHART_TYPES.COLUMN_STACKED_100, CHART_TYPES.NPS, CHART_TYPES.RANKING_FLOW].includes(chartType.value),
  );
  const isBar = computed(() =>
    [CHART_TYPES.BAR_CLUSTERED, CHART_TYPES.COLUMN_CLUSTERED].includes(chartType.value),
  );
  const isHorizontal = computed(() =>
    [CHART_TYPES.BAR_CLUSTERED, CHART_TYPES.BAR_STACKED_100, CHART_TYPES.NPS, CHART_TYPES.RANKING_FLOW].includes(chartType.value),
  );
  const isDoughnut = computed(() => chartType.value === CHART_TYPES.DOUGHNUT);
  const isHeatmap = computed(() => chartType.value === CHART_TYPES.HEATMAP);
  const isRadar = computed(() => chartType.value === CHART_TYPES.RADAR);
  const isPodium = computed(() => chartType.value === CHART_TYPES.PODIUM);
  const isDotPlot = computed(() => chartType.value === CHART_TYPES.DOT_PLOT);
  const isSingleSeries = computed(() => SINGLE_SERIES_CHART_TYPES.includes(chartType.value));

  const npsChoiceColors = Object.fromEntries(NPS_BUCKETS.map((b) => [b.code, b.color]));

  const displayStackedData = computed(() => {
    if (!isNpsChartType.value) return stackedData.value;
    return stackedData.value.map((row) => {
      const bucketed = {
        itemLabel: row.itemLabel,
        groupLabel: row.groupLabel,
        itemId: row.itemId,
        itemN: row.itemN,
        questionId: row.questionId,
        popId: row.popId,
        popColor: row.popColor,
        choiceColors: { ...npsChoiceColors, ...row.choiceColors },
      };
      NPS_BUCKETS.forEach((bucket) => {
        bucketed[`choice_${bucket.code}`] = bucket.codes.reduce((sum, code) => sum + (row[`choice_${code}`] || 0), 0);
        bucketed[`count_${bucket.code}`] = bucket.codes.reduce((sum, code) => sum + (row[`count_${code}`] || 0), 0);
        bucketed[`label_${bucket.code}`] = bucket.label;
        bucketed[`scores_${bucket.code}`] = bucket.codes.map((code) => ({
          code,
          value: row[`choice_${code}`] || 0,
          count: row[`count_${code}`] || 0,
        }));
      });
      return bucketed;
    });
  });

  const displayChoiceCodes = computed(() =>
    (isNpsChartType.value ? NPS_BUCKETS.map((b) => b.code) : matrixChoiceCodes.value),
  );

  const displayChoiceLabels = computed(() =>
    (isNpsChartType.value ? Object.fromEntries(NPS_BUCKETS.map((b) => [b.code, b.label])) : matrixChoiceLabels.value),
  );

  const showSignificance = computed(
    () => (mergedOptions.value.show_significance ?? true) && populationsBarData.value.length > 1,
  );

  const cardComment = computed(() => {
    const raw = mergedOptions.value.comment || "";
    const text = raw.replace(/<[^>]*>/g, "").trim();
    return text ? raw : "";
  });

  const displayName = computed(() => mergedOptions.value.custom_name_title?.name || cardName.value);
  const displayTitle = computed(() => mergedOptions.value.custom_name_title?.title || title.value);

  return {
    store,
    mergedOptions,
    questionMeta,
    effectiveQuestionId,
    cardType,
    codeLabels,
    isMatrix,
    isMatrixMultiple,
    cardName,
    populationsBarData,
    matrixItemsBarData,
    stackedData: displayStackedData,
    matrixChoiceCodes: displayChoiceCodes,
    matrixChoiceLabels: displayChoiceLabels,
    pieData,
    radarAxes,
    radarSeries,
    radarScale,
    matrixTableData,
    respondents,
    populationRespondents,
    displayedPopulations,
    singlePopulation,
    shouldIncludeWeights,
    title,
    activeIteration,
    loopIterationOptions,
    showSentiment,
    isNumeric,
    numericStats,
    downloadCardAsImage,
    copyCardToClipboard,
    copied,
    customBaseLabel,
    displayMode,
    chartTile,
    chartType,
    isNpsChartType,
    hasMultiplePopulations,
    isPie,
    isStacked,
    isBar,
    isHorizontal,
    isDoughnut,
    isHeatmap,
    isRadar,
    isPodium,
    isDotPlot,
    isSingleSeries,
    showSignificance,
    cardComment,
    displayName,
    displayTitle,
  };
}
